/**
 * FOCUSED - Main Theme JavaScript
 * Handles cart drawer, abandoned cart toast, and interactive elements
 */

(function () {
  "use strict";

  // ========================================
  // GA4 E-COMMERCE EVENT LAYER
  // ========================================
  // Pushes GA4-standard event names to window.dataLayer for GTM / GA4 to
  // consume. Ported from design-reference/cart.js, but hooked into this
  // theme's REAL Shopify cart calls (/cart/add.js, /cart/change.js) rather
  // than the prototype's localStorage model.
  //
  // No-ops harmlessly when no container is installed: pushes queue on
  // window.dataLayer and are read whenever GTM loads. The Klaviyo (_learnq),
  // Meta (fbq) and TikTok (ttq) tracking already in this file is untouched —
  // GA4 runs alongside it, it does not replace it.
  const CURRENCY =
    window.Shopify && window.Shopify.currency
      ? window.Shopify.currency.active
      : "USD";

  function nbTrack(event, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, payload || {}));
  }

  // Shopify money values are integer cents.
  function money(cents) {
    return typeof cents === "number" ? cents / 100 : 0;
  }

  function lineToItem(line) {
    return {
      item_id: String(line.product_id || line.id || ""),
      item_name: line.product_title || line.title || "",
      item_variant: line.variant_title || "",
      price: money(line.price),
      quantity: line.quantity || 1,
    };
  }

  window.nbTrack = nbTrack;

  // Most recent cart snapshot; begin_checkout fires as the page navigates
  // away, so there is no time for an async /cart.js round trip.
  let lastCart = null;

  // ========================================
  // CART DRAWER FUNCTIONALITY
  // ========================================
  const cartDrawer = {
    drawer: document.getElementById("cart-drawer"),
    overlay: null,
    closeBtn: null,
    lastFocused: null,
    cartIcon: document.getElementById("cart-icon"),
    cartCount: document.getElementById("cart-count"),

    init() {
      if (!this.drawer) return;

      this.overlay = this.drawer.querySelector(".cart-drawer__overlay");
      this.closeBtn = this.drawer.querySelector(".cart-drawer__close");

      // Event listeners
      if (this.cartIcon) {
        this.cartIcon.addEventListener("click", () => this.open());
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener("click", () => this.close());
      }

      if (this.overlay) {
        this.overlay.addEventListener("click", () => this.close());
      }

      // ESC key to close
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.drawer.classList.contains("active")) {
          this.close();
        }
      });

      // Keeps Tab inside the open dialog. aria-modal hides outside content
      // from screen readers but does NOT constrain the keyboard — this does.
      document.addEventListener("keydown", (e) => this.trapFocus(e));

      // Load cart on init
      this.loadCart();
    },

    async open() {
      this.lastFocused = document.activeElement; // remember the trigger
      this.drawer.hidden = false; // clear the markup's hidden attr
      this.drawer.classList.add("active");
      document.body.style.overflow = "hidden";
      const _cart = await this.loadCart();
      if (_cart) {
        nbTrack("view_cart", {
          ecommerce: {
            currency: CURRENCY,
            value: money(_cart.total_price),
            items: (_cart.items || []).map(lineToItem),
          },
        });
      }
      // Focus the close button so the keyboard lands inside the dialog.
      (this.closeBtn || this.drawer).focus();
    },

    close() {
      this.drawer.classList.remove("active");
      this.drawer.hidden = true;
      document.body.style.overflow = "";
      // Return focus to whatever opened it, per ARIA dialog practice.
      if (this.lastFocused && document.contains(this.lastFocused)) {
        this.lastFocused.focus();
      }
      this.lastFocused = null;
    },

    trapFocus(e) {
      if (e.key !== "Tab" || !this.drawer.classList.contains("active")) return;
      const f = this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!f.length) return;
      const first = f[0],
        last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },

    async loadCart() {
      try {
        const response = await fetch("/cart.js");
        const cart = await response.json();
        this.updateCartUI(cart);
        return cart;
      } catch (error) {
        console.error("Error loading cart:", error);
        return null;
      }
    },

    updateCartUI(cart) {
      // Update cart count
      if (this.cartCount) {
        this.cartCount.textContent = cart.item_count;
        this.cartCount.style.display = cart.item_count > 0 ? "flex" : "none";
      }

      // Update cart items
      const cartItemsContainer = document.getElementById("cart-items");
      if (!cartItemsContainer) return;

      if (cart.items.length === 0) {
        cartItemsContainer.innerHTML =
          '<p style="text-align: center; color: var(--text-secondary); padding: 2rem;">Your cart is empty</p>';
        return;
      }

      cartItemsContainer.innerHTML = cart.items
        .map(
          (item) => `
        <div class="cart-item" data-line="${item.line}">
          <img src="${item.image}" alt="${item.title}" style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px;">
          <div style="flex: 1; margin-left: 1rem;">
            <div style="font-weight: 600; margin-bottom: 0.25rem;">${item.product_title}</div>
            <div style="font-size: 0.875rem; color: var(--text-secondary);">${item.variant_title || ""}</div>
            <div style="font-family: var(--font-mono); color: var(--gold); margin-top: 0.5rem;">${this.formatMoney(item.price)}</div>
          </div>
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <button class="cart-item-qty-btn" data-line="${item.line}" data-action="decrease">−</button>
            <span style="min-width: 30px; text-align: center;">${item.quantity}</span>
            <button class="cart-item-qty-btn" data-line="${item.line}" data-action="increase">+</button>
          </div>
        </div>
      `,
        )
        .join("");

      lastCart = cart;

      // Update subtotal
      const subtotalEl = document.getElementById("cart-subtotal");
      if (subtotalEl) {
        subtotalEl.textContent = this.formatMoney(cart.total_price);
      }

      // Add quantity button listeners
      document.querySelectorAll(".cart-item-qty-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          const line = e.target.dataset.line;
          const action = e.target.dataset.action;
          const currentItem = cart.items.find((item) => item.line == line);

          if (currentItem) {
            const newQty =
              action === "increase"
                ? currentItem.quantity + 1
                : currentItem.quantity - 1;
            this.updateQuantity(line, newQty, currentItem);
          }
        });
      });
    },

    async updateQuantity(line, quantity, prevItem) {
      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ line, quantity }),
        });
        const cart = await response.json();
        this.updateCartUI(cart);

        // GA4 — only a genuine decrease is a removal.
        if (prevItem && quantity < prevItem.quantity) {
          const removed = prevItem.quantity - quantity;
          const item = lineToItem(prevItem);
          item.quantity = removed;
          nbTrack("remove_from_cart", {
            ecommerce: {
              currency: CURRENCY,
              value: money(prevItem.price) * removed,
              items: [item],
            },
          });
        }
      } catch (error) {
        console.error("Error updating cart:", error);
      }
    },

    formatMoney(cents) {
      return "$" + (cents / 100).toFixed(2);
    },
  };

  // Initialize cart drawer
  cartDrawer.init();

  // ========================================
  // ABANDONED CART TOAST
  // ========================================
  const abandonedCartToast = {
    toast: document.getElementById("abandoned-cart-toast"),
    delay: 18000, // 18 seconds
    timeout: null,
    hasShown: false,

    init() {
      if (!this.toast) return;

      // Show toast when items are in cart after delay
      this.checkCart();

      // Close button
      const closeBtn = this.toast.querySelector(".abandoned-cart-toast__close");
      if (closeBtn) {
        closeBtn.addEventListener("click", () => this.hide());
      }

      // Listen for cart changes
      document.addEventListener("cart:updated", () => {
        this.checkCart();
      });
    },

    async checkCart() {
      try {
        const response = await fetch("/cart.js");
        const cart = await response.json();

        if (cart.item_count > 0 && !this.hasShown) {
          this.scheduleShow();
        }
      } catch (error) {
        console.error("Error checking cart:", error);
      }
    },

    scheduleShow() {
      if (this.timeout) clearTimeout(this.timeout);

      this.timeout = setTimeout(() => {
        this.show();
      }, this.delay);
    },

    show() {
      this.toast.classList.remove("hidden");
      this.hasShown = true;
    },

    hide() {
      this.toast.classList.add("hidden");
    },
  };

  // Initialize abandoned cart toast
  abandonedCartToast.init();

  // ========================================
  // ADD TO CART FUNCTIONALITY
  // ========================================
  document.addEventListener("click", async function (e) {
    if (
      e.target.matches("[data-add-to-cart]") ||
      e.target.closest("[data-add-to-cart]")
    ) {
      e.preventDefault();
      const btn = e.target.matches("[data-add-to-cart]")
        ? e.target
        : e.target.closest("[data-add-to-cart]");
      const variantId = btn.dataset.variantId;

      if (!variantId) return;

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
          }),
        });

        if (response.ok) {
          const item = await response.json();

          // Track with Klaviyo
          if (typeof _learnq !== "undefined") {
            _learnq.push([
              "track",
              "Added to Cart",
              {
                $value: item.price / 100,
                ProductName: item.product_title,
                ProductID: item.product_id,
                SKU: item.sku,
                Quantity: item.quantity,
              },
            ]);
          }

          // Track with Meta Pixel
          if (typeof fbq !== "undefined") {
            fbq("track", "AddToCart", {
              content_name: item.product_title,
              content_ids: [item.product_id],
              content_type: "product",
              value: item.price / 100,
              currency: "USD",
            });
          }

          // Track with TikTok Pixel
          if (typeof ttq !== "undefined") {
            ttq.track("AddToCart", {
              content_name: item.product_title,
              content_id: item.product_id,
              content_type: "product",
              value: item.price / 100,
              currency: "USD",
            });
          }

          // GA4
          nbTrack("add_to_cart", {
            ecommerce: {
              currency: CURRENCY,
              value: money(item.price) * (item.quantity || 1),
              items: [lineToItem(item)],
            },
          });

          // Open cart drawer
          cartDrawer.open();

          // Dispatch custom event
          document.dispatchEvent(new CustomEvent("cart:updated"));
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
      }
    }
  });

  // ========================================
  // SMOOTH SCROLL FOR ANCHOR LINKS
  // ========================================
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener("click", function (e) {
      const href = this.getAttribute("href");
      if (href === "#") return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  });

  // ========================================
  // SCROLL ANIMATIONS (Fade In)
  // ========================================
  const observerOptions = {
    threshold: 0.1,
    rootMargin: "0px 0px -50px 0px",
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("fade-in");
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  // Observe sections for fade-in animation
  document.querySelectorAll(".section").forEach((section) => {
    observer.observe(section);
  });

  // ========================================
  // NUMBER COUNTER ANIMATION (Social Proof)
  // ========================================
  // prefix/suffix preserve any non-digit characters around the number, e.g. the
  // "%" in "100%". Writing back the bare integer silently dropped them, turning
  // "100% Third-Party Tested" into a meaningless "100" once the element scrolled
  // into view — visible only after the observer fired, never in the served HTML.
  function animateCounter(element, target, prefix = "", suffix = "") {
    const duration = 2000;
    const start = 0;
    const increment = target / (duration / 16);
    let current = start;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        element.textContent = prefix + target.toLocaleString() + suffix;
        clearInterval(timer);
      } else {
        element.textContent =
          prefix + Math.floor(current).toLocaleString() + suffix;
      }
    }, 16);
  }

  // Animate social proof numbers when in view
  const socialProofObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const parts = entry.target.textContent.trim().match(/^(\D*)([\d,]+)(\D*)$/);
          if (parts) {
            const number = parts[2].replace(/,/g, "");
            if (number && !isNaN(number)) {
              animateCounter(entry.target, parseInt(number), parts[1], parts[3]);
            }
          }
          socialProofObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.5 },
  );

  document.querySelectorAll(".social-proof__number").forEach((el) => {
    const hasNumber = /\d/.test(el.textContent);
    if (hasNumber && !el.textContent.includes("★")) {
      socialProofObserver.observe(el);
    }
  });

  // ========================================
  // GA4 — begin_checkout / capture_lead
  // ========================================
  // Delegated so it covers the drawer's checkout link, the cart page's
  // submit button, and any future checkout affordance without re-binding.
  document.addEventListener(
    "click",
    function (e) {
      const t = e.target.closest(
        'a[href^="/checkout"], button[name="checkout"], [data-checkout]',
      );
      if (!t) return;
      nbTrack("begin_checkout", {
        ecommerce: {
          currency: CURRENCY,
          value: lastCart ? money(lastCart.total_price) : undefined,
          items: lastCart ? (lastCart.items || []).map(lineToItem) : [],
        },
      });
    },
    true,
  );

  // Any newsletter / customer form on the page (footer, email popup).
  document.addEventListener("submit", function (e) {
    const form = e.target;
    if (!form || !form.querySelector) return;
    if (form.querySelector('input[name="contact[email]"]')) {
      nbTrack("capture_lead", {
        has_cart: !!(lastCart && lastCart.item_count > 0),
      });
    }
  });

})();

// ========================================
// GLOBAL UTILITY FUNCTIONS
// ========================================
window.FocusedTheme = {
  formatMoney(cents) {
    return "$" + (cents / 100).toFixed(2);
  },
};
